import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { FloorPlanFileOrientation, useFloorPlanTemplates } from '../_main'
import { FloorPlanRenderType, FloorPlanSelectionType, useFloorPlanConfig } from '../_main/FloorPlanConfigStep.context'
import React, { useCallback } from 'react'
import { usePurchaseFlowConfig, usePurchaseFlowProducts, useTargetOrderUser } from 'components/pages/PurchaseFlow/_main/contexts'

import { Box } from '@mui/material'
import { CONST_PRODUCT_SERIAL } from 'constants/product'
import { Color } from 'constants/assets'
import { FloorPlanColors } from 'constants/floorPlan'
import { FloorPlanConfigOptions } from 'constants/floorPlan'
import { FloorPlanFormat } from '../FloorPlanLayout'
import { Language } from 'translations/Language'
import ReactLoading from 'react-loading'
import { SelectableImageBox } from 'components/common/FloorPlanControls'
import Stack from '@mui/material/Stack'
import { Styles } from 'constants/floorPlan'
import { usePurchaseSessionVisualOperations } from 'utils/hooks/usePurchaseSessionVisualOperations'
import { useFloorPlanLogo } from '../_main/FloorPlanLogo.context'
import { logger } from 'utils/logging'

/**
 * @component
 * FloorPlanTemplates displays user available templates
 * 
 * @example
 * <FloorPlanTemplates />
 */
export const FloorPlanTemplates: React.FC = () => {

  const { uploadVisual: uploadPSVisual } = usePurchaseSessionVisualOperations()
  const { targetUser, adminSelectedUserEmail } = useTargetOrderUser()
  const { sessionId, selectedCategory } = usePurchaseFlowConfig()
  const { isTemplateLogoUploading } = useFloorPlanLogo()
  const { floorPlanProduct } = usePurchaseFlowProducts()
  const {
    selectedTemplateId,
    selectedColor,
    selectedStyle,
    showCompass,
    showDimensionChain,
    showFixtures,
    showFurniture,
    showRoomArea,
    showRoomNames,
    showScalebar,
    floorPlanFormat,
    outputLanguage,
    floorPlanTemplateType,
    showDisclaimer,
    disclaimerText,
    addGardensAndRoofSlopes,
    setOutputLanguage,
    setFileOrientation,
    setFloorPlanFormat,
    setCompanyLogo,
    setIsActiveLogo,
    setSelectedTemplateId,
    setSelectedColor,
    setSelectedStyle,
    setCurrentFloorPlanRenderType,
    setShowCompass,
    setShowDimensionChain,
    setShowFixtures,
    setShowFurniture,
    setShowRoomArea,
    setShowRoomNames,
    setShowScalebar,
    setFloorPlanTemplateType,
    setIsActiveDimensions,
    setShowDisclaimer,
    setDisclaimerText,
    setAddGardensAndRoofSlopes
  } = useFloorPlanConfig()

  const { availableTemplates, floorPlanTemplates } = useFloorPlanTemplates()

  const handleTemplateLogoUpload = useCallback((logoFile: File) => {
    if (!sessionId || !floorPlanProduct) return

    uploadPSVisual({ sessionId, productId: floorPlanProduct.id, productSerial: CONST_PRODUCT_SERIAL, file: logoFile, email: adminSelectedUserEmail })
  }, [adminSelectedUserEmail, uploadPSVisual, floorPlanProduct, sessionId])

  const applyTemplateConfig = useCallback((config: FloorPlanConfigOptions, renderType: FloorPlanRenderType) => {
    if (config.showCompass !== showCompass) setShowCompass(config.showCompass ?? false)
    if (config.showDimensionChain !== showDimensionChain) setShowDimensionChain(config.showDimensionChain ?? false)
    if (config.showFixtures !== showFixtures) setShowFixtures(config.showFixtures ?? false)
    if (config.showFurniture !== showFurniture) setShowFurniture(config.showFurniture ?? false)
    if (config.showGardensAndRoofSlopes !== addGardensAndRoofSlopes) setAddGardensAndRoofSlopes(config.showGardensAndRoofSlopes ?? false)
    if (config.showRoomArea !== showRoomArea) setShowRoomArea(config.showRoomArea ?? false)
    if (config.showRoomNames !== showRoomNames) setShowRoomNames(config.showRoomNames ?? false)
    if (config.showScalebar !== showScalebar) setShowScalebar(config.showScalebar ?? false)
    if (config.outputLanguage !== outputLanguage) setOutputLanguage(Language[(config.outputLanguage as Language)] ?? Language[(targetUser.language as Language)] ?? Language.EN)
    if (config.floorPlanFormat !== floorPlanFormat) setFloorPlanFormat(config.floorPlanFormat ?? { [FloorPlanFormat.JPG]: FloorPlanFormat.JPG })
    if (config.showDisclaimer !== showDisclaimer) setShowDisclaimer(config.showDisclaimer ?? false)
    if (config.disclaimerText !== disclaimerText) setDisclaimerText(config.disclaimerText ?? '')

    if (config.companyLogo && config.templateLogoFile) {
      setIsActiveLogo(true)
      setCompanyLogo(config.templateLogoFile.name)
      handleTemplateLogoUpload(config.templateLogoFile)
    } else {
      setIsActiveLogo(false)
      setCompanyLogo(null)
    }

    if (renderType === FloorPlanRenderType.RENDER_2D) {
      if (config.style !== selectedColor) setSelectedColor(config.style as FloorPlanColors)
      setFileOrientation(new Set(config.fileOrientation ?? [FloorPlanFileOrientation.HORIZONTAL])) // Have Horizontal as backup default so at least one is selected
    } else {
      if (config.style !== selectedStyle) setSelectedStyle(config.style as Styles)
    }

    if (config.showDimensionChain || config.showRoomArea) {
      setIsActiveDimensions(true)
    } else {
      setIsActiveDimensions(false)
    }

  }, [addGardensAndRoofSlopes, disclaimerText, floorPlanFormat, handleTemplateLogoUpload, outputLanguage, selectedColor, selectedStyle, setAddGardensAndRoofSlopes, setCompanyLogo, setDisclaimerText, setFileOrientation, setFloorPlanFormat, setIsActiveDimensions, setIsActiveLogo, setOutputLanguage, setSelectedColor, setSelectedStyle, setShowCompass, setShowDimensionChain, setShowDisclaimer, setShowFixtures, setShowFurniture, setShowRoomArea, setShowRoomNames, setShowScalebar, showCompass, showDimensionChain, showDisclaimer, showFixtures, showFurniture, showRoomArea, showRoomNames, showScalebar, targetUser.language])

  const handleSelectTemplate = (templateId: string, config: FloorPlanConfigOptions, renderType: FloorPlanRenderType) => {
    setSelectedTemplateId(templateId)
    setCurrentFloorPlanRenderType(renderType)
    // TODO: Add 3D defaulting when implemented
    applyTemplateConfig(
      {
        ...config,
        style: renderType === FloorPlanRenderType.RENDER_2D && !config.style ? FloorPlanColors.BLACK : config.style
      },
      renderType
    )

    if (floorPlanTemplateType !== FloorPlanSelectionType.TEMPLATE) {
      setFloorPlanTemplateType(FloorPlanSelectionType.TEMPLATE)
    }

    logAnalyticsEvent(AnalyticsEvent.FLOOR_PLAN_TEMPLATE_SELECTED, {
      templateId,
      type: renderType,
      category: selectedCategory,
    })
  }

  // Checked after the hooks, which must run on every render.
  if (!sessionId) {
    logger.warn('FloorPlanTemplates rendered without a session', undefined, {
      context: { component: 'FloorPlanTemplates', action: 'render' },
    })
    return null
  }

  return (
    <Stack marginTop={2} direction="row" justifyContent="center" alignItems="center" gap={2} sx={{ flexFlow: 'row wrap' }} minWidth="40rem">
      {Object.values(availableTemplates).map(({ id, name, imagePlaceholder, renderType, config }) =>
        <SelectableImageBox
          key={id}
          text={name}
          width={16.8}
          height={14.4}
          disabled={isTemplateLogoUploading}
          elevation='xs'
          image={(floorPlanTemplates?.isFetching) ? <Box marginTop={6}><ReactLoading color={Color.GRAY_TEXT} type="spin" width="2rem" height="2rem" /></Box> : imagePlaceholder}
          inactiveBorderColor='transparent'
          isSelected={id === selectedTemplateId}
          onSelect={() => !isTemplateLogoUploading && handleSelectTemplate(id, config, renderType)}
        />
      )}
    </Stack>
  )
}
